import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/core/close';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Inline } from '@atlaskit/primitives';
import { ModalTitle } from '@atlaskit/modal-dialog';

import { ReverseTrialLozengeExperiment } from '@confluence/trial-components';
import { ConfluenceEdition } from '@confluence/session-data';

import { OverflowMenu, type OverflowMenuProps } from './OverflowMenu';

export type TitleAndButtonsProps = {
	title: keyof typeof titles;
	overflowMenu: OverflowMenuProps | undefined;
	onBackClick?: () => void; // back button only displayed if this is present
	onCloseClick: () => void;
};

const titles = defineMessages({
	share: {
		id: 'share-and-restrict-dialog.header.title.share',
		defaultMessage: 'Share',
		description: 'Modal dialog title for the share and restrict dialog',
	},
	publicLink: {
		id: 'share-and-restrict-dialog.header.title.public-link',
		defaultMessage: 'Public link',
		description: 'Text is for the title of the Public link dialog',
	},
	slack: {
		id: 'share-and-restrict-dialog.header.title.slack',
		defaultMessage: 'Share to Slack',
		description: 'Text is for the title of the Share to Slack dialog',
	},
});

const i18n = defineMessages({
	close: {
		id: 'share-and-restrict-dialog.header.close',
		defaultMessage: 'Close',
		description: 'Screen-reader label for button that closes a modal dialog',
	},
	back: {
		id: 'share-and-restrict-dialog.header.back',
		defaultMessage: 'Back',
		description: 'Screen-reader label for button that goes back to the previous screen',
	},
});

export const TitleAndButtons = ({
	title,
	overflowMenu,
	onCloseClick,
	onBackClick,
}: TitleAndButtonsProps) => {
	const { formatMessage } = useIntl();
	return (
		<Inline grow="fill" spread="space-between">
			<Inline grow="fill" alignBlock="center" space="space.050">
				{onBackClick && (
					<IconButton
						icon={ArrowLeftIcon}
						label={formatMessage(i18n.back)}
						appearance="subtle"
						onClick={onBackClick}
					/>
				)}
				<ModalTitle>
					{title === 'publicLink' ? (
						<ReverseTrialLozengeExperiment
							touchpointId="confluence_public_link_dialog_header"
							edition={ConfluenceEdition.STANDARD}
						>
							<FormattedMessage {...titles[title]} />
						</ReverseTrialLozengeExperiment>
					) : (
						<FormattedMessage {...titles[title]} />
					)}
				</ModalTitle>
			</Inline>

			<Inline space="space.050">
				{overflowMenu && <OverflowMenu {...overflowMenu} />}
				<IconButton
					icon={EditorCloseIcon}
					label={formatMessage(i18n.close)}
					appearance="subtle"
					onClick={onCloseClick}
				/>
			</Inline>
		</Inline>
	);
};
